import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { api, bootstrapSession, setAccessToken, setOnAuthLost } from "../api/client";
import type { TokenResponse, User } from "../api/types";

interface AuthState {
  user: User | null;
  status: "loading" | "authed" | "anon";
  login: (email: string, password: string) => Promise<User>;
  register: (email: string, password: string) => Promise<void>;
  registerStudyId: (studyId: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>("loading");

  const refreshUser = useCallback(async () => {
    try {
      const me = await api<User>("GET", "/auth/me");
      setUser(me);
      setStatus("authed");
    } catch {
      setUser(null);
      setStatus("anon");
    }
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<User> => {
    const res = await api<TokenResponse>("POST", "/auth/login", {
      json: { email, password },
      auth: false,
    });
    setAccessToken(res.access_token);
    // Fetch the profile directly (rather than via refreshUser) so the caller
    // can route by role immediately after login.
    const me = await api<User>("GET", "/auth/me");
    setUser(me);
    setStatus("authed");
    return me;
  }, []);

  const register = useCallback(async (email: string, password: string) => {
    // No auto-login: the account can't sign in until its email is verified
    // (the backend gates login on verification). The caller shows a
    // "check your inbox" confirmation instead.
    await api<User>("POST", "/auth/register", {
      json: { email, password },
      auth: false,
    });
  }, []);

  const registerStudyId = useCallback(async (studyId: string, password: string) => {
    // Study-ID signup (the only substudy path): no email, no verification
    // gate. The caller sends the user to the login form next.
    await api<User>("POST", "/auth/register/study-id", {
      json: { study_id: studyId, password },
      auth: false,
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      await api<void>("POST", "/auth/logout", { auth: false });
    } catch {
      // best-effort; clear local state regardless
    }
    setAccessToken(null);
    setUser(null);
    setStatus("anon");
  }, []);

  useEffect(() => {
    setOnAuthLost(() => {
      setUser(null);
      setStatus("anon");
    });
    // The access token lives in memory only, so boot redeems the httpOnly
    // refresh cookie first (one request for anonymous visitors, two for a
    // signed-in reload) and fetches the profile only on success. A reload
    // stays signed in without touching storage.
    void (async () => {
      if (await bootstrapSession()) {
        await refreshUser();
      } else {
        setStatus("anon");
      }
    })();
  }, [refreshUser]);

  const value = useMemo<AuthState>(
    () => ({ user, status, login, register, registerStudyId, logout, refreshUser }),
    [user, status, login, register, registerStudyId, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
